import { LowerCasePipe, UpperCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { CbDialogContainerModule } from '@app/shared/components/dialog/dialog-container/dialog-container.module';
import {
    catchError,
    defer,
    finalize,
    map,
    Observable,
    of,
    ReplaySubject,
    Subject,
    Subscription,
    switchMap,
    tap
} from 'rxjs';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

export interface IPdfViewerDialogItem {
    id: number;
    name: string;
    filename?: string;
    documentType?: string;
    groupId?: number;
    groupName?: string;
    /** Loading changes data (e.g. generates a report): asked in the sidebar, the caller asks for selectedItemId */
    confirmBeforeLoad?: { heading: string; message: string };
}

export interface IPdfViewerDialogGroup {
    key: string;
    name: string;
    items: IPdfViewerDialogItem[];
}

export interface IPdfViewerDialogData {
    items: IPdfViewerDialogItem[];
    /** Defaults to the first item without confirmation; an id missing from items opens nothing */
    selectedItemId?: number;
    loadPdf: (itemId: number) => Observable<Blob>;
    listTitle?: string;
}

const OTHER_GROUP_KEY = 'other';

const MAX_CACHE_SIZE_BYTES = 100 * 1024 * 1024;

@Component({
    selector: 'cb-pdf-viewer-dialog',
    standalone: true,
    imports: [
        CbDialogContainerModule,
        MatIcon,
        MatIconButton,
        MatListModule,
        MatTooltip,
        PdfViewerComponent,
        LowerCasePipe,
        UpperCasePipe
    ],
    templateUrl: './pdf-viewer-dialog.component.html',
    styleUrls: ['./pdf-viewer-dialog.component.scss']
})
export class PdfViewerDialogComponent implements OnInit, OnDestroy {
    public static readonly MIN_WIDTH = '80vw';

    public selectedItem: IPdfViewerDialogItem | null = null;
    public pdfBlob: Blob | null = null;
    /** Item whose file is in the viewer, lags behind selectedItem while the next one loads */
    private shownItemId: number | null = null;
    public loading = false;
    public loadCount = 0;
    public loadErrorDescription = '';
    public canRetryLoad = false;
    public sidebarCollapsed = false;
    public groups: IPdfViewerDialogGroup[] = [];
    public showGroupHeaders = false;
    public readonly expandedGroupKeys = new Set<string>();

    private readonly pdfCache = new Map<number, Blob>();
    private pdfCacheSize = 0;
    private readonly selection$ = new Subject<IPdfViewerDialogItem>();
    private selectionSubscription: Subscription | null = null;
    private readonly loads = new Map<number, Observable<Blob>>();
    private documentListElement: HTMLElement | null = null;

    /** Angular also calls this with the same list when groups are toggled, that must not scroll */
    @ViewChild('documentList', { read: ElementRef })
    public set documentList(list: ElementRef<HTMLElement> | undefined) {
        const listElement = list?.nativeElement ?? null;
        if (listElement && listElement !== this.documentListElement) {
            this.scrollToSelectedItem(listElement);
        }
        this.documentListElement = listElement;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: IPdfViewerDialogData,
        private readonly cbDialog: CbDialogService
    ) {}

    public get loadError(): boolean {
        return !!this.loadErrorDescription;
    }

    public get downloadFilename(): string | null {
        const filename =
            (this.pdfBlob instanceof File && this.pdfBlob.name) ||
            this.selectedItem?.filename ||
            this.selectedItem?.name;
        if (!filename) {
            return null;
        }
        return filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
    }

    public get listTitle(): string {
        return this.data.listTitle || 'Documents';
    }

    /** Only a temporary failure (no response, server error) can succeed when tried again */
    private showLoadError(error: unknown): void {
        const status = error instanceof HttpErrorResponse ? error.status : null;
        this.canRetryLoad = status === 0 || status >= 500;
        if (status === 0) {
            // Also a server error: the API's exception handler drops the CORS headers
            this.loadErrorDescription =
                'The server could not be reached or failed to return the file. Try again later.';
        } else if (status === 404) {
            this.loadErrorDescription = 'The file was not found or could not be generated.';
        } else if (status >= 500) {
            this.loadErrorDescription = `The server could not return the file (error ${status}). Try again later or contact support.`;
        } else {
            const code = status ? ` (error ${status})` : '';
            this.loadErrorDescription = `The file could not be loaded${code}. Contact support if the problem persists.`;
        }
    }

    public ngOnInit(): void {
        this.groups = this.buildGroups(this.data.items);
        this.showGroupHeaders = this.data.items.some(
            item => this.getGroupKey(item) !== OTHER_GROUP_KEY
        );

        this.selectionSubscription = this.selection$
            .pipe(
                switchMap(item => {
                    const cached = this.getFromCache(item.id);
                    const blob$: Observable<Blob | null> = cached ? of(cached) : this.getLoad(item);
                    return blob$.pipe(
                        catchError(error => {
                            this.showLoadError(error);
                            return of(null);
                        }),
                        map(blob => ({ item, blob }))
                    );
                })
            )
            .subscribe(({ item, blob }) => {
                this.pdfBlob = blob;
                this.shownItemId = blob ? item.id : null;
                if (!blob && !this.loadError) {
                    this.showLoadError(null);
                }
                this.loading = false;
            });

        const initialItem =
            this.data.selectedItemId != null
                ? this.data.items.find(item => item.id === this.data.selectedItemId)
                : this.data.items.find(item => !item.confirmBeforeLoad);
        if (initialItem) {
            this.expandedGroupKeys.add(this.getGroupKey(initialItem));
            this.open(initialItem);
        }
    }

    public ngOnDestroy(): void {
        this.selectionSubscription?.unsubscribe();
    }

    public getItemTitle(item: IPdfViewerDialogItem | null): string {
        if (!item) {
            return 'Document';
        }
        return item.documentType ? `${item.name}, Type: ${item.documentType}` : item.name;
    }

    public isGroupExpanded(group: IPdfViewerDialogGroup): boolean {
        return !this.showGroupHeaders || this.expandedGroupKeys.has(group.key);
    }

    public toggleGroup(group: IPdfViewerDialogGroup): void {
        if (this.expandedGroupKeys.has(group.key)) {
            this.expandedGroupKeys.delete(group.key);
        } else {
            this.expandedGroupKeys.add(group.key);
        }
    }

    public select(item: IPdfViewerDialogItem): void {
        if (item.id === this.selectedItem?.id) {
            return;
        }
        if (this.pdfCache.has(item.id) || this.loads.has(item.id)) {
            this.open(item);
            return;
        }
        this.confirmLoad(item, () => this.open(item));
    }

    /** pdf.js cannot open the file in the viewer, which may not be the selected item yet */
    public onViewerLoadingFailed(): void {
        const failedItemId = this.shownItemId;
        if (failedItemId == null) {
            return;
        }
        this.removeFromCache(failedItemId);
        this.shownItemId = null;
        if (this.loading || failedItemId !== this.selectedItem?.id) {
            return;
        }
        this.pdfBlob = null;
        this.loadErrorDescription = 'The file is not a valid PDF or could not be opened.';
    }

    public retry(): void {
        const item = this.selectedItem;
        if (item) {
            this.confirmLoad(item, () => this.load(item));
        }
    }

    public toggleSidebar(): void {
        this.sidebarCollapsed = !this.sidebarCollapsed;
    }

    private confirmLoad(item: IPdfViewerDialogItem, load: () => void): void {
        if (!item.confirmBeforeLoad) {
            load();
            return;
        }
        this.cbDialog.confirm({
            dialogHeading: item.confirmBeforeLoad.heading,
            message: item.confirmBeforeLoad.message,
            confirmed: load
        });
    }

    private open(item: IPdfViewerDialogItem): void {
        this.selectedItem = item;
        this.load(item);
    }

    /** The list has position: relative, so offsetTop is relative to it */
    private scrollToSelectedItem(list: HTMLElement): void {
        const selectedElement = list.querySelector<HTMLElement>(
            '.pdf-viewer-dialog__item--selected'
        );
        if (selectedElement) {
            list.scrollTop =
                selectedElement.offsetTop - (list.clientHeight - selectedElement.offsetHeight) / 2;
        }
    }

    private load(item: IPdfViewerDialogItem): void {
        this.loadErrorDescription = '';
        this.canRetryLoad = false;
        this.loading = true;
        this.loadCount++;
        this.selection$.next(item);
    }

    /**
     * HTTP requests are not cancelled, so a load runs to the end even when the user moves on, and its file is cached:
     * coming back does not download or generate it again.
     * Not unsubscribed when the dialog closes either: the app's request tracking (progress bar) only sees a request
     * finish through its subscriber, an abandoned one would stay "in progress" until reload.
     */
    private getLoad(item: IPdfViewerDialogItem): Observable<Blob> {
        let load$ = this.loads.get(item.id);
        if (!load$) {
            const result$ = new ReplaySubject<Blob>(1);
            load$ = result$.asObservable();
            this.loads.set(item.id, load$);
            defer(() => this.data.loadPdf(item.id))
                .pipe(
                    tap(blob => this.addToCache(item.id, blob)),
                    finalize(() => this.loads.delete(item.id))
                )
                .subscribe(result$);
        }
        return load$;
    }

    private buildGroups(items: IPdfViewerDialogItem[]): IPdfViewerDialogGroup[] {
        const groupsByKey = new Map<string, IPdfViewerDialogGroup>();
        for (const item of items) {
            const key = this.getGroupKey(item);
            let group = groupsByKey.get(key);
            if (!group) {
                group = {
                    key,
                    name: key === OTHER_GROUP_KEY ? 'Other' : item.groupName,
                    items: []
                };
                groupsByKey.set(key, group);
            }
            group.items.push(item);
        }
        return Array.from(groupsByKey.values()).sort((a, b) => {
            if (a.key === OTHER_GROUP_KEY || b.key === OTHER_GROUP_KEY) {
                return a.key === OTHER_GROUP_KEY ? 1 : -1;
            }
            return a.name.localeCompare(b.name);
        });
    }

    private getGroupKey(item: IPdfViewerDialogItem): string {
        return item.groupId != null && item.groupName ? `group-${item.groupId}` : OTHER_GROUP_KEY;
    }

    /** Re-inserted so the least recently viewed PDF is evicted first */
    private getFromCache(itemId: number): Blob | undefined {
        const blob = this.pdfCache.get(itemId);
        if (blob) {
            this.pdfCache.delete(itemId);
            this.pdfCache.set(itemId, blob);
        }
        return blob;
    }

    private addToCache(itemId: number, blob: Blob): void {
        if (blob.size > MAX_CACHE_SIZE_BYTES) {
            return;
        }
        this.removeFromCache(itemId);
        while (this.pdfCacheSize + blob.size > MAX_CACHE_SIZE_BYTES) {
            this.removeFromCache(this.pdfCache.keys().next().value);
        }
        this.pdfCache.set(itemId, blob);
        this.pdfCacheSize += blob.size;
    }

    private removeFromCache(itemId: number): void {
        const blob = this.pdfCache.get(itemId);
        if (blob) {
            this.pdfCache.delete(itemId);
            this.pdfCacheSize -= blob.size;
        }
    }
}
